import React, { useState, useEffect } from "react";
import { publicApi } from "../../../services";
import { Service, SubService } from "../../../types";
import { ActionButton, StepIndicatorHeader } from "../../shared";

interface ServiceSelectorProps {
  professionalId?: number;
  selectedService: Service | null;
  isCarCompany?: boolean;
  hasError?: boolean;
  onBackToDate?: () => void;
  onServiceChange: (service: Service) => void;
  selectedSubServices?: number[];
  onSubServicesChange?: (subServiceIds: number[]) => void;
  onContinue?: () => void;
}

export const ServiceSelector: React.FC<ServiceSelectorProps> = ({
  isCarCompany = false,
  professionalId,
  selectedService,
  hasError = false,
  onBackToDate,
  onServiceChange,
  selectedSubServices = [],
  onSubServicesChange,
  onContinue,
}) => {
  const [services, setServices] = useState<Service[]>([]);
  const [availableSubServices, setAvailableSubServices] = useState<
    SubService[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [subServicesLoading, setSubServicesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const response = await publicApi.getServicesByCompany();

        if (response.success && response.data) {
          // Filter by professional if professionalId is provided
          let filteredServices = response.data;
          if (professionalId) {
            filteredServices = response.data.filter(
              (service) =>
                service.professionalId === professionalId ||
                service.professionalId === null
            );
          }

          setServices(filteredServices);
          setError(null);
        } else {
          setError(response.error || "Failed to load services");
        }
      } catch (err) {
        setError("Failed to connect to services API");
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [professionalId]);

  // Fetch optional subservices when a service is selected
  useEffect(() => {
    const fetchOptionalSubServices = async () => {
      if (!selectedService) {
        setAvailableSubServices([]);
        return;
      }

      // Check if service has any optional subservice relations
      const optionalRelations =
        selectedService.subServiceRelations?.filter((rel) => !rel.isRequired) ||
        [];

      if (optionalRelations.length === 0) {
        setAvailableSubServices([]);
        return;
      }

      try {
        setSubServicesLoading(true);
        const response = await publicApi.getSubServicesByCompany();

        if (response.success && response.data && Array.isArray(response.data)) {
          // Only show subservices that are in the optional relations
          const optionalSubServiceIds = new Set(
            optionalRelations.map((rel) => rel.subServiceId)
          );

          const availableOptionalSubServices = response.data.filter((sub) => {
            return sub.isActive && optionalSubServiceIds.has(sub.id);
          });

          setAvailableSubServices(availableOptionalSubServices);
        } else {
          setAvailableSubServices([]);
        }
      } catch (err) {
        setAvailableSubServices([]);
      } finally {
        setSubServicesLoading(false);
      }
    };

    fetchOptionalSubServices();
  }, [selectedService]);

  const handleSubServiceToggle = (subServiceId: number) => {
    if (!onSubServicesChange) return;

    const isSelected = selectedSubServices.includes(subServiceId);
    if (isSelected) {
      onSubServicesChange(
        selectedSubServices.filter((id) => id !== subServiceId)
      );
    } else {
      onSubServicesChange([...selectedSubServices, subServiceId]);
    }
  };

  // Get the price for a subservice based on the selected service's relationships
  const getSubServicePrice = (subServiceId: number): number => {
    if (!selectedService) return 25.0;

    // Check if this subservice has a relationship with the selected service
    const relation = selectedService.subServiceRelations?.find(
      (rel) => rel.subServiceId === subServiceId
    );

    // Convert price to number since API returns strings
    return parseFloat(relation?.price?.toString() || "25.0");
  };

  // Calculate total price of selected subservices
  const getSelectedSubServicesTotal = (): number => {
    return selectedSubServices.reduce((total, subServiceId) => {
      return total + getSubServicePrice(subServiceId);
    }, 0);
  };

  return (
    <div className="space-y-6 animate-slide-up">
      <StepIndicatorHeader
        stepNumber={isCarCompany ? 2 : 3}
        title="Selecionar Serviço"
        description="Escolha o serviço desejado"
      />

      {error && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-xl mx-auto max-w-2xl">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-amber-400 text-xl">⚠️</span>
            </div>
            <div className="ml-3">
              <p className="text-sm text-amber-700">
                <strong>Usando serviços offline.</strong> {error}
              </p>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <div className="animate-spin w-8 h-8 border-4 border-gray-200 border-t-sky-500 rounded-full"></div>
          <p className="text-gray-600 font-medium">Carregando serviços...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {services.map((service) => (
            <button
              key={service.id}
              onClick={() => {
                onServiceChange(service);
              }}
              className={`group relative overflow-hidden rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 text-left transition-all duration-300 transform hover:scale-105 ${
                selectedService?.id === service.id
                  ? "bg-gradient-to-br from-sky-500 to-gray-600 text-white shadow-large ring-2 sm:ring-4 ring-sky-200"
                  : "bg-white hover:bg-sky-50 border-2 border-gray-200 hover:border-sky-300 shadow-soft hover:shadow-medium"
              }`}
            >
              {/* Background decoration */}
              <div
                className={`absolute top-0 right-0 w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full transform translate-x-3 -translate-y-3 sm:translate-x-4 sm:-translate-y-4 md:translate-x-6 md:-translate-y-6 transition-colors duration-300 ${
                  selectedService?.id === service.id
                    ? "bg-white/10"
                    : "bg-sky-50"
                }`}
              ></div>

              <div className="relative z-10 space-y-2 sm:space-y-3 md:space-y-4">
                <div className="flex items-start justify-between">
                  <h4
                    className={`font-bold text-base sm:text-lg leading-tight ${
                      selectedService?.id === service.id
                        ? "text-white"
                        : "text-gray-800"
                    }`}
                  >
                    {service.name}
                  </h4>
                  <div
                    className={`flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center ${
                      selectedService?.id === service.id
                        ? "border-white bg-white"
                        : "border-gray-300 group-hover:border-sky-500"
                    }`}
                  >
                    {selectedService?.id === service.id && (
                      <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-sky-600 rounded-full"></div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
                  {service.type === "time" && (
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm font-medium ${
                        selectedService?.id === service.id
                          ? "bg-white/20 text-white"
                          : "bg-sky-100 text-sky-700"
                      }`}
                    >
                      <span>⏱️</span>
                      {service.duration} min
                    </span>
                  )}
                  <span
                    className={`text-lg sm:text-xl font-bold ${
                      selectedService?.id === service.id
                        ? "text-white"
                        : "text-sky-600"
                    }`}
                  >
                    R${" "}
                    {parseFloat(service.price.toString())
                      .toFixed(2)
                      .replace(".", ",")}
                  </span>
                </div>

                {service.description && (
                  <p
                    className={`text-xs sm:text-sm leading-relaxed ${
                      selectedService?.id === service.id
                        ? "text-white/90"
                        : "text-gray-600"
                    }`}
                  >
                    {service.description}
                  </p>
                )}
              </div>

              {selectedService?.id === service.id && (
                <div className="absolute inset-0 bg-gradient-to-br from-transparent to-sky-600/20 pointer-events-none"></div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Customization Option - Show if there are available subservices OR if loading */}
      {selectedService && !hasError &&
        (availableSubServices.length > 0 || subServicesLoading) && (
          <div className="mt-8 animate-slide-up">
            <div className="text-center mb-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-2">
                Deseja personalizar seu atendimento?
              </h4>
              <p className="text-gray-600 text-sm">
                Adicione serviços extras para uma experiência completa
              </p>
            </div>

            {subServicesLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin w-5 h-5 border-2 border-gray-200 border-t-sky-500 rounded-full"></div>
                <span className="ml-2 text-gray-600 text-sm">
                  Carregando opções...
                </span>
              </div>
            ) : (
              <div className="space-y-3 max-w-2xl mx-auto">
                {availableSubServices.map((subService) => {
                  const isSelected = selectedSubServices.includes(
                    subService.id
                  );
                  const price = getSubServicePrice(subService.id);

                  return (
                    <div
                      key={subService.id}
                      onClick={() => handleSubServiceToggle(subService.id)}
                      className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                        isSelected
                          ? "bg-sky-50 border-sky-500"
                          : "bg-white border-gray-200 hover:border-sky-300 hover:shadow-sm"
                      }`}
                    >
                      {/* Checkbox */}
                      <div className="flex-shrink-0 mr-4">
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            isSelected
                              ? "bg-sky-600 border-sky-600"
                              : "border-gray-300"
                          }`}
                        >
                          {isSelected && (
                            <svg
                              className="w-3 h-3 text-white"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <h5
                              className={`font-medium ${
                                isSelected ? "text-sky-900" : "text-gray-800"
                              }`}
                            >
                              {subService.name}
                            </h5>
                            {subService.description && (
                              <p
                                className={`text-sm mt-1 ${
                                  isSelected ? "text-sky-700" : "text-gray-600"
                                }`}
                              >
                                {subService.description}
                              </p>
                            )}
                          </div>
                          <div className="ml-4">
                            <span
                              className={`text-sm font-semibold px-2 py-1 rounded ${
                                isSelected
                                  ? "bg-sky-100 text-sky-800"
                                  : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              + R${" "}
                              {parseFloat(price.toString())
                                .toFixed(2)
                                .replace(".", ",")}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Total with customizations */}
            {selectedSubServices.length > 0 && (
              <div className="mt-6 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg max-w-md mx-auto">
                <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                  <span>Serviço principal:</span>
                  <span>
                    R${" "}
                    {parseFloat(selectedService.price.toString())
                      .toFixed(2)
                      .replace(".", ",")}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                  <span>Personalizações ({selectedSubServices.length}):</span>
                  <span>
                    + R${" "}
                    {getSelectedSubServicesTotal().toFixed(2).replace(".", ",")}
                  </span>
                </div>
                <hr className="my-2 border-gray-300" />
                <div className="flex items-center justify-between font-bold text-sky-800">
                  <span>Total:</span>
                  <span>
                    R${" "}
                    {(
                      parseFloat(selectedService.price.toString()) +
                      getSelectedSubServicesTotal()
                    )
                      .toFixed(2)
                      .replace(".", ",")}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

      {/* Continue Button - Show whenever a service is selected */}
      {selectedService && !hasError && onContinue && (
        <div className="mt-8 text-center">
          <ActionButton
            onClick={onContinue}
            variant="primary"
            size="md"
            className="inline-flex items-center gap-2"
          >
            Continuar
            <span className="text-lg">→</span>
          </ActionButton>
        </div>
      )}

      {hasError && (
        <div className="mt-8 text-center">
        <ActionButton
          onClick={onBackToDate}
          variant="primary"
          size="md"
          className="inline-flex items-center gap-2"
        >
          Escolher outra data
          <span className="text-lg">→</span>
        </ActionButton>
      </div>
      )}
    </div>
  );
};
